import React, { useState } from 'react';
import { SuccessIcon, ErrorIcon } from '../../../icons';
import { Goal, MilestoneCategory } from '../types';
import {
  CapabilityDomain,
  CapabilityDecision,
  CAPABILITY_DOMAIN_LABELS,
} from './types';
import { createMilestone } from '../firebase/milestones.service';
import { createAction } from '../firebase/actions.service';
import { saveAssessment } from './capabilityStorage.service';
import CapabilityDomainStep from './CapabilityDomainStep';
import DomainMilestoneCard, { StagedMilestone } from './DomainMilestoneCard';
import MilestonesPriorityReview from './MilestonesPriorityReview';
import {
  getQuestionsFlow,
  getFirstQuestionId,
  getNextQuestionId,
  formatQuestion,
  ExtendedQuestion,
} from './extendedQuestionsConfig';

// =============================================================================
// TYPES
// =============================================================================

interface CapabilityWizardV2Props {
  goal: Goal;
  ownerId: string;
  onComplete: () => void;
  onCancel: () => void;
  onMilestoneCreated?: (milestoneId: string) => void;
}

const ALL_DOMAINS: CapabilityDomain[] = [
  'strategy',
  'marketing',
  'sales',
  'product',
  'operations',
  'finance',
  'team',
];

type WizardStep = 
  | 'domain_decision'       // Basic capability questions
  | 'extended_questions'    // Follow-up questions (budget, etc.)
  | 'milestone_review'      // Per-domain milestone card
  | 'priority_review'       // Final overview & reordering
  | 'saving';               // Saving to Firebase

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * CapabilityWizardV2 - Redesigned per-domein flow
 * 
 * New Flow:
 * 1. Voor elk domein:
 *    a. Basic capability questions (existing)
 *    b. Extended questions (budget-aware, etc.)
 *    c. Milestone card review (user edits/confirms)
 * 2. After all domains: Priority review (reorder, final confirm)
 * 3. Save all to Firebase
 */
const CapabilityWizardV2: React.FC<CapabilityWizardV2Props> = ({
  goal,
  ownerId,
  onComplete,
  onCancel,
  onMilestoneCreated,
}) => {
  // State
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [wizardStep, setWizardStep] = useState<WizardStep>('domain_decision');
  const [decisions, setDecisions] = useState<CapabilityDecision[]>(() =>
    ALL_DOMAINS.map(domain => ({
      domain,
      required: undefined as any,
    }))
  );
  const [extendedAnswers, setExtendedAnswers] = useState<Record<string, Record<string, any>>>({});
  const [currentExtendedQuestionId, setCurrentExtendedQuestionId] = useState<string | null>(null);
  const [stagedMilestones, setStagedMilestones] = useState<StagedMilestone[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentDomain = ALL_DOMAINS[currentDomainIndex];
  const currentDecision = decisions[currentDomainIndex];
  const currentDomainAnswers = extendedAnswers[currentDomain] || {};
  
  // ============================================================================
  // DOMAIN DECISION HANDLERS
  // ============================================================================
  
  const handleUpdateDecision = (updates: Partial<CapabilityDecision>) => {
    setDecisions(prev => {
      const updated = [...prev];
      updated[currentDomainIndex] = {
        ...updated[currentDomainIndex],
        ...updates,
      };
      return updated;
    });
  };
  
  const handleDomainDecisionComplete = () => {
    // Check if decision is complete
    if (!currentDecision.required || !currentDecision.resolution) {
      setError('Maak eerst alle keuzes voor dit domein.');
      return;
    }
    
    // Check if this resolution needs extended questions
    const questionsFlow = getQuestionsFlow(currentDecision.resolution);
    
    if (Object.keys(questionsFlow).length > 0) {
      // Start extended questions
      const firstQuestionId = getFirstQuestionId(currentDecision.resolution);
      setCurrentExtendedQuestionId(firstQuestionId);
      setWizardStep('extended_questions');
    } else {
      // No extended questions, go directly to milestone review
      setWizardStep('milestone_review');
    }
    
    setError(null);
  };
  
  // ============================================================================
  // EXTENDED QUESTIONS HANDLERS
  // ============================================================================
  
  const handleExtendedAnswer = (questionId: string, answer: any) => {
    // Save answer
    setExtendedAnswers(prev => ({
      ...prev,
      [currentDomain]: {
        ...prev[currentDomain],
        [questionId]: answer
      }
    }));
    
    // Get next question
    if (!currentDecision.resolution) {
      console.error('Resolution is not set');
      return;
    }
    const questionsFlow = getQuestionsFlow(currentDecision.resolution as 'learn' | 'outsource' | 'hire');
    const nextQuestionId = getNextQuestionId(questionId, answer, questionsFlow);
    
    if (nextQuestionId) {
      setCurrentExtendedQuestionId(nextQuestionId);
    } else {
      // Extended questions complete, go to milestone review
      setWizardStep('milestone_review');
    }
  };
  
  const handleExtendedBack = () => {
    // For now, just go back to domain decision
    // TODO: Implement proper question history navigation
    setWizardStep('domain_decision');
    setCurrentExtendedQuestionId(null);
  };
  
  // ============================================================================
  // MILESTONE REVIEW HANDLERS
  // ============================================================================
  
  const handleMilestoneSave = (milestone: StagedMilestone) => {
    // Add to staged milestones
    setStagedMilestones(prev => [...prev, milestone]);
    
    // Move to next domain or final review
    if (currentDomainIndex < ALL_DOMAINS.length - 1) {
      // Next domain
      setCurrentDomainIndex(prev => prev + 1);
      setWizardStep('domain_decision');
      setCurrentExtendedQuestionId(null);
    } else {
      // All domains done, show priority review
      setWizardStep('priority_review');
    }
  };
  
  const handleMilestoneBack = () => {
    // Go back to extended questions or domain decision
    if (currentExtendedQuestionId) {
      setWizardStep('extended_questions');
    } else {
      setWizardStep('domain_decision');
    }
  };
  
  // ============================================================================
  // PRIORITY REVIEW HANDLERS
  // ============================================================================
  
  const handleReorder = (reordered: StagedMilestone[]) => {
    setStagedMilestones(reordered);
  };
  
  const handleEditMilestone = (index: number) => {
    // Go back to that domain's milestone review
    const milestone = stagedMilestones[index];
    const domainIndex = ALL_DOMAINS.indexOf(milestone.domain);
    
    // Remove from staged
    setStagedMilestones(prev => prev.filter((_, i) => i !== index));
    
    // Go back to that domain
    setCurrentDomainIndex(domainIndex);
    setWizardStep('milestone_review');
  };
  
  const handleFinalConfirm = async () => {
    setIsSaving(true);
    setWizardStep('saving');
    setError(null);
    
    try {
      // 1. Save capability assessment
      await saveAssessment(ownerId, goal.id, {
        decisions,
        completedAt: Date.now(),
      });
      
      // 2. Save all milestones + actions
      for (const staged of stagedMilestones) {
        // Create milestone
        const milestoneId = await createMilestone(ownerId, goal.id, {
          title: staged.title,
          targetDate: staged.endDate,
          category: mapDomainToMilestoneCategory(staged.domain),
          origin: 'capability',
          fromSparring: false,
        });
        
        // Notify parent
        onMilestoneCreated?.(milestoneId);
        
        // Create actions
        for (const action of staged.actions) {
          await createAction(ownerId, goal.id, milestoneId, {
            title: action.title,
            completed: false,
            priority: staged.priority,
            domain: staged.domain,
            origin: 'milestone_generated',
          });
        }
      }
      
      // Success!
      onComplete();
    } catch (err: any) {
      console.error('Failed to save milestones:', err);
      setError(err.message || 'Er ging iets mis bij het opslaan.');
      setIsSaving(false);
      setWizardStep('priority_review');
    }
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  const progress = ((currentDomainIndex + (wizardStep === 'domain_decision' ? 0 : 0.5)) / ALL_DOMAINS.length) * 100;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      {wizardStep === 'domain_decision' && (
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-500 to-indigo-500">
            <h2 className="text-2xl font-bold text-white">
              Capaciteiten checken
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Stap {currentDomainIndex + 1} van {ALL_DOMAINS.length}: {CAPABILITY_DOMAIN_LABELS[currentDomain]}
            </p>
          </div>
          
          {/* Progress */}
          <div className="px-6 py-3 bg-gray-50 border-b">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-500 to-pink-500 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <CapabilityDomainStep
              decision={currentDecision}
              onUpdate={handleUpdateDecision}
            />
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                {error}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            {currentDomainIndex > 0 ? (
              <button
                onClick={() => setCurrentDomainIndex(prev => prev - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Vorige
              </button>
            ) : (
              <button
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Annuleren
              </button>
            )}
            
            <button
              onClick={handleDomainDecisionComplete}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold"
            >
              Volgende →
            </button>
          </div>
        </div>
      )}
      
      {wizardStep === 'extended_questions' && currentExtendedQuestionId && (
        <ExtendedQuestionsStep
          domain={currentDomain}
          decision={currentDecision}
          questionId={currentExtendedQuestionId}
          answers={currentDomainAnswers}
          onAnswer={handleExtendedAnswer}
          onBack={handleExtendedBack}
        />
      )}
      
      {wizardStep === 'milestone_review' && (
        <DomainMilestoneCard
          domain={currentDomain}
          decision={currentDecision}
          extendedAnswers={currentDomainAnswers}
          goalContext={{
            title: goal.title,
            description: goal.description,
            startDate: goal.startDate,
            endDate: goal.endDate,
          }}
          onSave={handleMilestoneSave}
          onBack={handleMilestoneBack}
        />
      )}
      
      {wizardStep === 'priority_review' && (
        <MilestonesPriorityReview
          stagedMilestones={stagedMilestones}
          goalTitle={goal.title}
          onReorder={handleReorder}
          onEdit={handleEditMilestone}
          onConfirm={handleFinalConfirm}
          onBack={() => {
            // Go back to last domain
            setCurrentDomainIndex(ALL_DOMAINS.length - 1);
            setWizardStep('milestone_review');
          }}
        />
      )}
      
      {wizardStep === 'saving' && (
        <div className="bg-white rounded-lg shadow-2xl p-12 max-w-md text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Mijlpalen worden opgeslagen...
          </h3>
          <p className="text-gray-600 text-sm">
            Even geduld, we slaan alles op
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface ExtendedQuestionsStepProps {
  domain: CapabilityDomain;
  decision: CapabilityDecision;
  questionId: string;
  answers: Record<string, any>;
  onAnswer: (questionId: string, answer: any) => void;
  onBack: () => void;
}

const ExtendedQuestionsStep: React.FC<ExtendedQuestionsStepProps> = ({
  domain,
  decision,
  questionId,
  answers,
  onAnswer,
  onBack,
}) => {
  const [currentAnswer, setCurrentAnswer] = useState<any>('');
  
  // Type guard for resolution
  if (!decision.resolution) {
    return <div>Resolution is not set</div>;
  }
  
  const questionsFlow = getQuestionsFlow(decision.resolution as 'learn' | 'outsource' | 'hire');
  const question = questionsFlow[questionId];
  
  if (!question) {
    return <div>Question not found: {questionId}</div>;
  }
  
  const formattedQuestion = formatQuestion(question.question, domain);
  
  const handleSubmit = () => {
    onAnswer(questionId, currentAnswer);
    setCurrentAnswer('');
  };
  
  return (
    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-500 to-pink-500">
        <h2 className="text-2xl font-bold text-white">
          {CAPABILITY_DOMAIN_LABELS[domain]}
        </h2>
        <p className="text-purple-100 text-sm mt-1">
          Extra vragen - {decision.resolution === 'outsource' ? 'Uitbesteden' : decision.resolution === 'learn' ? 'Leren' : 'Aannemen'}
        </p>
      </div>
      
      {/* Content */}
      <div className="p-8">
        <label className="block text-lg font-semibold text-gray-800 mb-4">
          {formattedQuestion}
        </label>
        
        {question.type === 'boolean' && (
          <div className="space-y-3">
            <button
              onClick={() => {
                setCurrentAnswer(true);
                setTimeout(() => handleSubmit(), 100);
              }}
              className="w-full p-4 border-2 border-gray-200 hover:border-green-500 
                       hover:bg-green-50 rounded-lg text-left transition-all"
            >
              <SuccessIcon size={14} className="inline" /> Ja
            </button>
            <button
              onClick={() => {
                setCurrentAnswer(false);
                setTimeout(() => handleSubmit(), 100);
              }}
              className="w-full p-4 border-2 border-gray-200 hover:border-red-500 
                       hover:bg-red-50 rounded-lg text-left transition-all"
            >
              <ErrorIcon size={14} className="inline" /> Nee
            </button>
          </div>
        )}
        
        {question.type === 'currency' && (
          <div>
            <div className="relative">
              <span className="absolute left-4 top-3 text-gray-500">€</span>
              <input
                type="number"
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(Number(e.target.value))}
                placeholder={question.placeholder}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={!currentAnswer}
              className="mt-4 w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 
                       text-white rounded-lg font-semibold disabled:opacity-50"
            >
              Volgende →
            </button>
          </div>
        )}
        
        {question.type === 'radio' && question.options && (
          <div className="space-y-3">
            {question.options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setCurrentAnswer(option.value);
                  setTimeout(() => handleSubmit(), 200);
                }}
                className="w-full p-4 border-2 border-gray-200 hover:border-orange-500 
                         hover:bg-orange-50 rounded-lg text-left transition-all"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        
        {question.type === 'textarea' && (
          <div>
            <textarea
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              placeholder={question.placeholder}
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <button
              onClick={handleSubmit}
              disabled={!currentAnswer.trim()}
              className="mt-4 w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 
                       text-white rounded-lg font-semibold disabled:opacity-50"
            >
              Volgende →
            </button>
          </div>
        )}
        
        {/* Checkbox and other types can be added later */}
      </div>
      
      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
        >
          ← Terug
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function mapDomainToMilestoneCategory(domain: CapabilityDomain): MilestoneCategory {
  switch (domain) {
    case 'strategy':
      return 'richting_keuzes';
    case 'marketing':
      return 'zichtbaarheid_markt';
    case 'sales':
      return 'doorbraak_uitvoering';
    case 'product':
      return 'vaardigheden_kennis';
    case 'operations':
      return 'systemen_structuur';
    case 'finance':
      return 'systemen_structuur';
    case 'team':
      return 'opschaling_hefboom';
    default:
      return 'systemen_structuur';
  }
}

export default CapabilityWizardV2;
